import { trigger, transition, style, animate } from "@angular/animations";
import { Component, Input, OnInit } from "@angular/core";
import { Account, Client, ObjectProperty } from "@Models";
import { AccountSettingsService } from "@Services";
import { SharedModule } from "@Shared/shared.module";

@Component({
    selector: 'advanced-settings',
    host: { 'class': 'd-flex flex-row flex-fill' },
    templateUrl: './advanced-settings.component.html',
    styleUrls: ['../account-settings-v2.component.scss', './advanced-settings.component.scss'],
    animations: [
        trigger('flyRightToLeft', [
            transition(':enter', [
                style({ height: 0, opacity: 0 }),
                animate('200ms ease-out',
                    style({ height: 70, opacity: 1 }))
            ]),
            transition(':leave', [
                style({ height: 70, opacity: 1 }),
                animate('200ms ease-in',
                    style({ height: 0, opacity: 0 }))
            ])
        ])
    ],
    imports: [SharedModule]
})
export class AdvancedSettingsComponent implements OnInit {
    @Input() client: Client;
    @Input() account: Account;

    objectProperties: ObjectProperty[] = [];
    editingStates: { [key: string]: boolean } = {};
    jsonErrors: { [key: string]: string } = {};
    isLoading = false;
    isSaving = false;

    constructor(private accountSettingsService: AccountSettingsService) {
        
    }

    ngOnInit(): void {
        this.loadObjectProperties();
    }

    loadObjectProperties() {
        this.isLoading = true;
        this.accountSettingsService.loadObjectProperties(this.account).subscribe({
            next: (properties) => {
                this.objectProperties = properties.sort((a, b) => a.key.localeCompare(b.key));
                this.objectProperties = this.filterObjectProperties();
                this.isLoading = false;
            },
            error: (error) => {
                console.error('Error loading object properties:', error);
                this.isLoading = false;
            }
        });
    }

    saveObjectProperties() {
        if (this.hasJsonErrors()) {
            return;
        }

        this.isSaving = true;
        this.accountSettingsService.saveObjectProperties(this.account, this.objectProperties).subscribe({
            next: (updatedProperties) => {
                this.objectProperties = updatedProperties.sort((a, b) => a.key.localeCompare(b.key));
                this.objectProperties = this.filterObjectProperties();
                this.editingStates = {};
                this.isSaving = false;
            },
            error: (error) => {
                console.error('Error saving object properties:', error);
                this.isSaving = false;
            }
        });
    }

    filterObjectProperties() : ObjectProperty[] {
        const removedKeys = ['sectionSettings', 'sectionRoleSetting', 'DocumentFormattingOptions'];
        return this.objectProperties.filter(prop => !removedKeys.includes(prop.key));
    }

    toggleEdit(propertyId: string) {
        this.editingStates[propertyId] = !this.editingStates[propertyId];
        if (this.editingStates[propertyId]) {
            // Format JSON when entering edit mode
            const property = this.objectProperties.find(p => p.id === propertyId);
            if (property) {
                property.value = this.formatJson(property.value);
            }
        }
    }

    cancelEdit(propertyId: string) {
        this.editingStates[propertyId] = false;
        delete this.jsonErrors[propertyId];
        // Reload the original value
        this.loadObjectProperties();
    }

    onJsonChange(propertyId: string, value: string) {
        const property = this.objectProperties.find(p => p.id === propertyId);
        if (property) {
            property.value = value;
            this.validateJson(propertyId, value);
        }
    }

    validateJson(propertyId: string, jsonString: string) {
        try {
            if (jsonString.trim()) {
                JSON.parse(jsonString);
            }
            delete this.jsonErrors[propertyId];
        } catch (error) {
            this.jsonErrors[propertyId] = 'Invalid JSON format';
        }
    }

    formatJson(jsonString: string): string {
        try {
            if (!jsonString || !jsonString.trim()) {
                return '';
            }
            const parsed = JSON.parse(jsonString);
            return JSON.stringify(parsed, null, 2);
        } catch (error) {
            // Return original string if parsing fails
            return jsonString;
        }
    }

    isEditing(propertyId: string): boolean {
        return !!this.editingStates[propertyId];
    }

    hasJsonError(propertyId: string): boolean {
        return !!this.jsonErrors[propertyId];
    }

    getJsonError(propertyId: string): string {
        return this.jsonErrors[propertyId] || '';
    }

    hasJsonErrors(): boolean {
        return Object.keys(this.jsonErrors).length > 0;
    }

    getDisplayValue(value: string): string {
        if (!value) return 'No value';
        try {
            const parsed = JSON.parse(value);
            return JSON.stringify(parsed, null, 2);
        } catch (error) {
            return value;
        }
    }

    trackByPropertyId(index: number, property: ObjectProperty): string {
        return property.id;
    }
}